import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Youtube, Sun, BookOpen, Video, Sparkles, X } from 'lucide-react';

/**
 * Welcome overlay shown exactly once when a brand-new push subscriber taps
 * the "Welcome home 🙏" notification — the SW opens `/?welcome=1`, we show
 * this three-step tour, and then quietly strip the query string.
 *
 * Design decisions:
 *  • Lives in a React Portal-style fixed overlay rather than a route so the
 *    user still lands on the real Home page under the hood.
 *  • Dismisses cleanly with the keyboard (Esc), the X button, the backdrop,
 *    OR by tapping any of the tour chips (which navigate the user into the
 *    feature they tapped).
 *  • Writes to localStorage so refreshing the page with `?welcome=1` still
 *    in the URL won't re-show it — the push URL is only ever opened once
 *    per notification, but better safe than annoying.
 */
const SHOWN_KEY = 'moj-welcome-shown-at';

const TOUR_ITEMS = [
  {
    to: '/live',
    title: 'Watch LIVE',
    desc: 'Join the Sunday service and prayer streams from anywhere in the world.',
    icon: Youtube,
  },
  {
    to: '/morning-prayer',
    title: 'Morning Prayer',
    desc: 'Pray with us every Monday at 5:00 AM — one hour before the day begins.',
    icon: Sun,
  },
  {
    to: '/leaflets',
    title: 'Reading Room',
    desc: 'Flip through gospel leaflets as a real 3D booklet, right in your browser.',
    icon: BookOpen,
  },
  {
    to: '/media',
    title: 'Daily Devotion',
    desc: "Watch today's devotion and catch up on past teachings any time.",
    icon: Video,
  },
];


export default function WelcomeOverlay() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Decide whether to show the overlay. We check the URL param on every
  // location change so hitting the browser back/forward or opening a shared
  // link with ?welcome=1 does the right thing.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('welcome') !== '1') { setOpen(false); return; }
    try {
      const prev = localStorage.getItem(SHOWN_KEY);
      // Reshow if it's been more than 30 days since the last welcome, since
      // that's long enough that the user likely forgot and the tour is still
      // helpful (e.g. we rolled out new features).
      if (prev && Date.now() - Number(prev) < 30 * 24 * 60 * 60 * 1000) {
        // Still clean the URL so the ?welcome param doesn't linger in share links.
        navigate(location.pathname + location.hash, { replace: true });
        return;
      }
    } catch (_) { /* localStorage disabled — show anyway */ }
    setOpen(true);
  }, [location.search, location.pathname, location.hash, navigate]);

  const close = useCallback(() => {
    try { localStorage.setItem(SHOWN_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setOpen(false);
    // Strip ?welcome=1 so it won't retrigger on reload.
    const params = new URLSearchParams(location.search);
    params.delete('welcome');
    const qs = params.toString();
    navigate(location.pathname + (qs ? `?${qs}` : '') + location.hash, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  // Esc to close + body scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      data-testid="welcome-overlay"
    >
      <div className="relative max-w-md w-full card-moj p-6 sm:p-7 shadow-2xl animate-in zoom-in-95 duration-200">
        <button
          type="button"
          aria-label="Close welcome"
          onClick={close}
          className="absolute top-3 right-3 p-2 min-h-[36px] min-w-[36px] rounded-full text-moj-charcoal/60 hover:bg-moj-cream touch-manipulation"
          data-testid="welcome-close"
        >
          <X size={18}/>
        </button>

        <div className="text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-moj-purple to-moj-purple-deep text-moj-gold items-center justify-center mb-3 shadow-lg">
            <Sparkles size={26}/>
          </div>
          <h2 id="welcome-title" className="font-serif text-2xl text-moj-purple-deep leading-tight">
            Welcome home <span aria-hidden="true">🙏</span>
          </h2>
          <p className="text-sm text-moj-charcoal/80 mt-2">
            You're part of the Ministry of Jesus family now. Here's a quick tour — tap any card to jump in.
          </p>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2.5" data-testid="welcome-tour">
          {TOUR_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={close}
                className="group block rounded-xl bg-moj-cream border border-moj-gold/30 p-3 hover:border-moj-gold hover:shadow-md transition touch-manipulation"
                data-testid={`welcome-tour-${item.to.slice(1).replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="w-9 h-9 rounded-lg bg-moj-purple-deep text-moj-gold flex items-center justify-center mb-2 group-hover:scale-105 transition">
                  <Icon size={16}/>
                </div>
                <div className="font-semibold text-sm text-moj-purple-deep leading-tight">{item.title}</div>
                <div className="text-[11px] text-moj-charcoal/70 leading-snug mt-1 line-clamp-2">{item.desc}</div>
              </Link>
            );
          })}
        </div>

        <button
          type="button"
          onClick={close}
          className="btn-outline w-full mt-5"
          data-testid="welcome-dismiss"
        >
          I'll explore on my own
        </button>
      </div>
    </div>
  );
}
